import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning' | 'info' | 'glass';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'icon';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  icon,
  iconPosition = 'left',
  children,
  className = '',
  ...props
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const iconSide = iconPosition === 'left' ? (isRTL ? 'right' : 'left') : (isRTL ? 'left' : 'right');

  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all focus:outline-none focus-visible-ring disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden';
  
  const variantClasses = {
    primary: 'liquid-glass-button text-white shadow-glass hover:shadow-glass-strong hover:scale-105 active:scale-95',
    secondary: 'glass-button opacity-90 hover:opacity-100 shadow-md hover:shadow-lg',
    outline: 'border-2 border-white/30 text-white hover:bg-white/10 shadow-sm hover:shadow-md',
    ghost: 'text-white/80 hover:text-white hover:bg-white/10',
    danger: 'bg-red-500/90 hover:bg-red-600 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95',
    success: 'bg-green-500/90 hover:bg-green-600 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95',
    warning: 'bg-yellow-500/90 hover:bg-yellow-600 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95',
    info: 'bg-blue-500/90 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95',
    glass: 'glass-button text-white shadow-glass hover:shadow-glass-strong hover:scale-105 active:scale-95'
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm rounded-lg gap-1.5',
    md: 'px-4 py-2.5 text-base rounded-xl gap-2',
    lg: 'px-6 py-3 text-lg rounded-2xl gap-2.5',
    xl: 'px-8 py-4 text-xl rounded-3xl gap-3',
    icon: 'p-2 rounded-xl gap-0'
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} transition-fast ease-smooth ${className}`;

  const iconElement = icon && !loading && (
    <span className={`flex-shrink-0 ${iconSide === 'left' ? (isRTL ? 'ml-2' : 'mr-2') : (isRTL ? 'mr-2' : 'ml-2')}`}>
      {icon}
    </span>
  );

  const loadingElement = loading && (
    <span className={`flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`}>
      <Loader2 size={size === 'sm' ? 14 : size === 'lg' || size === 'xl' ? 20 : 16} className="animate-spin" />
    </span>
  );

  return (
    <button
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {iconPosition === 'left' && (loadingElement || iconElement)}
      <span>{children}</span>
      {iconPosition === 'right' && (loadingElement || iconElement)}
    </button>
  );
};

export default Button;
